/**
 * In-app profiler for the market-data hot paths. Entirely local: metrics are aggregated in memory
 * and handed to PerfMetricsState for inspection - nothing is sent to any service.
 *
 * Off unless `?debug=performance` is in the URL, and when off `measurePerf` costs one boolean
 * check before calling straight through.
 */
import { logConfig } from '../../configDebug';
import PerfMetricsState from '../store-util/PerfMetricsState';

type PerfMetadata = Record<string, unknown>;

type PerfBucket = {
  count: number;
  totalMs: number;
  maxMs: number;
  lastMetadata?: PerfMetadata;
  slowCount: number;
};

const PERF_LOG_INTERVAL_MS = 2000;
/** Anything at or above this is counted separately - a frame budget is ~16ms. */
const SLOW_SAMPLE_THRESHOLD_MS = 8;
const LOG_PREFIX = '[performance]';

const buckets: Record<string, PerfBucket> = {};
let flushTimer: ReturnType<typeof setTimeout> | null = null;

export const isPerfMetricsEnabled = (): boolean => Boolean(logConfig.enabled && logConfig.performance);

const now = (): number => (
  typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now()
);

export const flushPerfMetrics = (): void => {
  if (!isPerfMetricsEnabled()) {
    return;
  }

  const rows = Object.entries(buckets).map(([ name, bucket ]) => ({
    name,
    count: bucket.count,
    avgMs: Number((bucket.totalMs / bucket.count).toFixed(2)),
    maxMs: Number(bucket.maxMs.toFixed(2)),
    slowCount: bucket.slowCount,
    lastMetadata: bucket.lastMetadata,
  }));

  Object.keys(buckets).forEach(name => { delete buckets[name]; });

  if (rows.length) {
    PerfMetricsState.mergeSnapshot(rows, Date.now());
    console.warn(LOG_PREFIX, rows); // eslint-disable-line no-console
  }
};

const scheduleFlush = (): void => {
  if (flushTimer) {
    return;
  }

  flushTimer = setTimeout(() => {
    flushTimer = null;
    flushPerfMetrics();
  }, PERF_LOG_INTERVAL_MS);
};

export const recordPerfMetric = (name: string, durationMs: number, metadata?: PerfMetadata): void => {
  if (!isPerfMetricsEnabled()) {
    return;
  }

  const bucket = buckets[name] ?? {
    count: 0, totalMs: 0, maxMs: 0, slowCount: 0,
  };

  bucket.count += 1;
  bucket.totalMs += durationMs;
  bucket.maxMs = Math.max(bucket.maxMs, durationMs);
  bucket.lastMetadata = metadata;
  if (durationMs >= SLOW_SAMPLE_THRESHOLD_MS) {
    bucket.slowCount += 1;
  }
  buckets[name] = bucket;
  scheduleFlush();
};

/** Times `callback` under `name`. Returns whatever the callback returns, and rethrows as-is. */
export const measurePerf = <T>(name: string, metadata: PerfMetadata | undefined, callback: () => T): T => {
  if (!isPerfMetricsEnabled()) {
    return callback();
  }

  const startedAt = now();
  try {
    return callback();
  } finally {
    recordPerfMetric(name, now() - startedAt, metadata);
  }
};

/** Test-only: drops aggregated state so one test cannot observe another's samples. */
export const _resetPerfMetrics_InUnitTests = (): void => {
  Object.keys(buckets).forEach(name => { delete buckets[name]; });
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  PerfMetricsState.reset();
};
